import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput, CustomTextarea } from "@components/Forms";
import DocumentTable from "@components/Tables/DocumentTable";
import { Breadcrumb } from "@components/UI";
import ConfirmDeleteModal from "@components/UI/ConfirmDeleteModal";
import FullScreenDotLoader from "@components/UI/FullScreenDotLoader";
import LoadingSpinner from "@components/UI/LoadingSpinner";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useFileGroupAPI } from "@hooks/useFileGroupAPI";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, normalizeResponseData, uppercase } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { formatUserNameWithId } from "@utils/userDisplay";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import { CHARACTER_LENGTH } from "@constants/index";

const TEST_ID = "faq-detail";
const tid = (suffix) => `${TEST_ID}-${suffix}`;
const fieldContainerTestId = (name) => tid(`field-${name}-container`);
const fieldLabelTestId = (name) => tid(`field-${name}-label`);
const fieldInputTestId = (name) => tid(`field-${name}-input`);
const fieldValueTestId = (name) => tid(`field-${name}-value`);

export default function FAQDetail() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { faqSeq } = useParams();
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [faqData, setFaqData] = useState({});
  const [editingData, setEditingData] = useState({});
  const [isUnchanged, setIsUnchanged] = useState(true);
  const [hasErrors, setHasErrors] = useState(false);
  const [fileInfoModified, setFileInfoModified] = useState([]);
  const [documentCurrentPage, setDocumentCurrentPage] = useState(1);
  const [documentItemsPerPage] = useState(5);
  const { fileInfo, documentTotalData, getFileInformationPagination, uploadFilesAndGetOnSuccess } = useFileGroupAPI(
    faqData?.fileGroupNo || 0,
    documentCurrentPage || 1,
  );

  // API mutations
  const getFaqData = useMutationData(
    `${queryKeys.getFaqById}/${faqSeq}`,
    { method: "GET" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          const normalizedData = normalizeResponseData(response);
          setFaqData(normalizedData);
          setEditingData(normalizedData);
        }
      },
      onError: (error) => {
        if (error?.response?.status === 404) {
          navigate("/404", { state: { backTo: "/faq" } });
        } else {
          showErrorToast(t("bulletin.get-faq-error-title"));
        }
        console.error("Error getting FAQ data:", error);
      },
    },
  );

  const increaseViewCount = useMutationData(`${queryKeys.increaseViewCountFaq}/${faqSeq}`, { method: "PUT" });

  const updateFaq = useMutationData(
    `${queryKeys.updateFaq}/${faqSeq}`,
    { method: "PUT" },
    {
      onSuccess: async (response) => {
        if (typeof response !== "string") {
          await updateDataInIndexedDb(STORES.FAQS, response, response.faqSeq);
          showSuccessToast(t("bulletin.faq-update-success"));

          // Handle file upload if files are selected
          if (newFiles?.length > 0) {
            const uploadFilesData = newFiles.reduce((acc, file, index) => {
              if (file instanceof File) {
                acc[`file_${index}`] = file;
              }
              return acc;
            }, {});

            const formData = new FormData();
            formData.append("targetSeq", faqSeq);
            formData.append("targetTypeCd", "FAQ");
            formData.append("fileGroupNo", faqData.fileGroupNo);

            Object.entries(uploadFilesData).forEach(([key, file]) => {
              formData.append(key, file, file.name);
            });

            uploadFilesAndGetOnSuccess.mutate(formData);
          } else if (fileInfoSeqListToDelete?.length > 0) {
            getFileInformationPagination.mutate();
          }

          getFaqData.mutate();
          setIsEditMode(false);
          setIsUnchanged(true);
        }
      },
      onError: (error) => {
        console.error("Error updating FAQ:", error);
        showErrorToast(t("bulletin.faq-update-success"));
      },
    },
  );

  // API call for deleting files
  const deleteFile = useMutationData(
    (fileInfoSeq) => `file_info/${fileInfoSeq}`,
    {
      method: "DELETE",
    },
    {
      onSuccess: () => {
        showSuccessToast(t("project.document.delete-success"));
        // Refresh file list after deletion
        if (faqData.fileGroupNo && faqData.fileGroupNo !== 0) {
          getFileInformationPagination.mutate();
          // Calculate new maximum number of pages
          const maxPages = Math.ceil((documentTotalData - 1) / documentItemsPerPage);
          // If current page exceeds maxPages, switch to the last valid page
          if (documentCurrentPage > maxPages && maxPages > 0) {
            setDocumentCurrentPage(maxPages);
          } else if (documentTotalData <= 1) {
            // If no files remain, reset to page 1
            setDocumentCurrentPage(1);
          }
        }
      },
      onError: (error) => {
        console.error("Error during delete file:", error);
        showErrorToast(t("project.document.delete-error"));
      },
    },
  );

  useEffect(() => {
    getFaqData.mutateAsync();
  }, []);

  // Effects
  useEffect(() => {
    if (faqSeq) {
      increaseViewCount.mutateAsync();
    }
  }, [faqSeq]);

  useEffect(() => {
    if (faqData.fileGroupNo && faqData.fileGroupNo !== 0) {
      getFileInformationPagination.mutate();
    }
  }, [faqData.fileGroupNo, documentCurrentPage]);

  useEffect(() => {
    // Calculate the maximum number of pages based on documentTotalData
    const maxPages = Math.ceil((documentTotalData || 0) / documentItemsPerPage);

    // If documentCurrentPage exceeds maxPages, switch to the last valid page
    if (documentCurrentPage > maxPages && maxPages > 0) {
      setDocumentCurrentPage(maxPages);
    } else if (documentTotalData === 0) {
      // If there are no files left, reset to page 1
      setDocumentCurrentPage(1);
    }
  }, [documentTotalData, documentItemsPerPage, documentCurrentPage]);

  useEffect(() => {
    setFileInfoModified(fileInfo);
  }, [fileInfo]);

  // File management logic
  const validFiles = fileInfoModified.filter((file) => file.fileInfoSeq || file.file);
  const hasBoth = validFiles.filter((file) => file.fileInfoSeq && file.file);
  const newFiles = validFiles.map((file) => file.file).filter(Boolean);
  const deletedFiles = fileInfo.filter((orig) => !validFiles.some((file) => file.fileInfoSeq === orig.fileInfoSeq));
  const fileInfoSeqListToDelete = [
    ...hasBoth.map((file) => file.fileInfoSeq),
    ...deletedFiles.map((file) => file.fileInfoSeq),
  ];

  useEffect(() => {
    setIsUnchanged(hasBoth.length === 0 && newFiles.length === 0 && deletedFiles.length === 0 && isUnchanged);
  }, [fileInfoModified]);

  // Validation
  useEffect(() => {
    const hasRequiredFields = editingData.question?.trim();

    // Compare specific fields instead of using JSON.stringify
    const dataChanged = editingData.question !== faqData.question || editingData.answer !== faqData.answer;
    setHasErrors(!hasRequiredFields);
    setIsUnchanged(!dataChanged);
  }, [editingData, faqData]);

  // Check if save button should be enabled
  const canSave = editingData.question?.trim() && !isUnchanged;

  // Handlers
  const handleEdit = () => {
    setIsEditMode(true);
    setEditingData({ ...faqData });
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditingData({ ...faqData });
    setFileInfoModified(fileInfo);
  };

  const handleSave = () => {
    if (!hasErrors && !isUnchanged) {
      updateFaq.mutate({
        ...faqData,
        ...editingData,
      });
    }
  };

  const handleInputChange = (field, value) => {
    setEditingData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // File management functions
  const handleUploadFile = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.multiple = true;
    fileInput.accept = "*/*";
    fileInput.onchange = (event) => {
      const files = Array.from(event.target.files);
      if (files.length > 0) {
        if (!faqSeq) {
          showErrorToast("Missing FAQ sequence.");
          return;
        }

        const uploadFilesData = files.reduce((acc, file, index) => {
          if (file instanceof File) {
            acc[`file_${index}`] = file;
          }
          return acc;
        }, {});

        const formData = new FormData();
        formData.append("targetSeq", faqSeq);
        formData.append("targetTypeCd", "FAQ");
        formData.append("fileGroupNo", faqData.fileGroupNo);

        Object.entries(uploadFilesData).forEach(([key, file]) => {
          formData.append(key, file, file.name);
        });

        uploadFilesAndGetOnSuccess.mutate(formData, {
          onSuccess: () => {
            // Refresh announcementData to update fileGroupNo
            getFaqData.mutateAsync().then(() => {
              // Refresh file list with the current page
              getFileInformationPagination.mutate();
              // Calculate new number of pages based on updated documentTotalData
              const maxPages = Math.ceil((documentTotalData + files.length) / documentItemsPerPage);
              // Only update documentCurrentPage if the current page is invalid
              if (documentCurrentPage > maxPages && maxPages > 0) {
                setDocumentCurrentPage(maxPages);
              } else if (documentTotalData === 0 && files.length > 0) {
                setDocumentCurrentPage(1); // Reset to page 1 if there were no files before
              }
              // Otherwise, do not change documentCurrentPage
            });
          },
          onError: (error) => {
            console.error("Error during upload:", error);
            showErrorToast(t("common.files-upload-error"));
          },
        });
      } else {
        showErrorToast("No files selected");
      }
    };

    fileInput.click();
  };

  // Document delete modal state
  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);

  const handleDeleteDocument = (documentToDelete) => {
    setDocumentToDelete(documentToDelete);
    setShowDeleteDocumentModal(true);
  };

  const handleConfirmDeleteDocument = () => {
    if (!documentToDelete) return;
    setDeleteDocumentLoading(true);

    deleteFile.mutate(documentToDelete.fileInfoSeq, {
      onSettled: () => {
        setDeleteDocumentLoading(false);
        setShowDeleteDocumentModal(false);
        setDocumentToDelete(null);
      },
    });
  };

  if (getFaqData.isPending) {
    return (
      <div data-testid="faq-detail-loading">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={`${colors.bgSecondary}`} data-testid="faq-detail-page">
      {/* Header */}
      <div className="mb-6" data-testid="faq-detail-header">
        {/* Breadcrumb */}
        <Breadcrumb
          data-testid="faq-detail-breadcrumb"
          items={[
            { label: t("navbar.faq-list"), href: "/faq" },
            {
              label: t("navbar.faq-details"),
              href: null,
              onClick: isEditMode ? () => setIsEditMode(false) : undefined,
            },
            ...(isEditMode ? [{ label: t("bulletin.edit-faq") }] : []),
          ]}
          className="mb-4"
        />

        {/* Title and Action Buttons */}
        <div className="flex items-center justify-between" data-testid="faq-detail-title-row">
          <h1
            className={`text-2xl font-bold truncate ${colors.textPrimary}`}
            data-testid="faq-detail-page-title"
          >
            {faqData?.question || "LOADING..."}
          </h1>
        </div>
      </div>

      {/* Main Information Cards */}
      <div
        className={`rounded-2xl border ${colors.borderPrimary} ${colors.bgPrimary}`}
        data-testid="faq-detail-information-card"
      >
        <div className="flex flex-col lg:flex-row" data-testid="faq-detail-information-layout">
          {/* FAQ Information Card */}
          <div className={`w-full p-6`} data-testid="faq-detail-information-body">
            <div className="flex items-center justify-between mb-2" data-testid="faq-detail-information-header-row">
              <h2
                className={`text-lg font-bold ${colors.textPrimary}`}
                data-testid="faq-detail-information-title"
              >
                {uppercase(t("bulletin.faq-information"))}
              </h2>
              {isEditMode ? (
                <div className="flex items-center justify-end space-x-3" data-testid="faq-detail-edit-actions">
                  <OutlineButton data-testid="faq-detail-cancel-button" onClick={handleCancel}>
                    {t("common.cancel-button")}
                  </OutlineButton>
                  <PrimaryButton data-testid="faq-detail-save-button" onClick={handleSave} disabled={!canSave}>
                    {t("common.save-button")}
                  </PrimaryButton>
                </div>
              ) : (
                <OutlineButton data-testid="faq-detail-edit-button" onClick={handleEdit}>
                  {t("project.edit-information")}
                </OutlineButton>
              )}
            </div>
            {!isEditMode ? (
              <div className={`pt-4`} data-testid="faq-detail-content-view">
                <div data-testid={fieldContainerTestId("question")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("question")}>
                    {t("bulletin.question")}
                  </label>
                  <CustomTextarea
                    data-testid={fieldValueTestId("question")}
                    rows={4}
                    value={editingData.question}
                    title={editingData.question}
                    readOnly={true}
                  />
                </div>
                <div className="md:col-span-2 mt-4" data-testid={fieldContainerTestId("answer")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("answer")}>
                    {t("bulletin.answer")}
                  </label>
                  <CustomTextarea
                    data-testid={fieldValueTestId("answer")}
                    rows={4}
                    value={editingData.answer}
                    title={editingData.answer}
                    readOnly={true}
                  />
                </div>
              </div>
            ) : (
              <div className={`pt-4`} data-testid="faq-detail-form">
                <div data-testid={fieldContainerTestId("question")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("question")}>
                    {t("bulletin.question")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomTextarea
                    data-testid={fieldInputTestId("question")}
                    rows={4}
                    placeholder={t("bulletin.enter-question")}
                    value={editingData.question}
                    onChange={(e) => handleInputChange("question", e.target.value)}
                    title={editingData.question}
                    maxLength={CHARACTER_LENGTH.CONTENT}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("question-char-count")}>
                    {(editingData.question || "").length}/{CHARACTER_LENGTH.CONTENT}
                  </p>
                </div>
                <div className="md:col-span-2 mt-6" data-testid={fieldContainerTestId("answer")}>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`} data-testid={fieldLabelTestId("answer")}>
                    {t("bulletin.answer")}
                  </label>
                  <CustomTextarea
                    data-testid={fieldInputTestId("answer")}
                    rows={4}
                    placeholder={t("bulletin.enter-answer")}
                    value={editingData.answer}
                    onChange={(e) => handleInputChange("answer", e.target.value)}
                    title={editingData.answer}
                    maxLength={CHARACTER_LENGTH.CONTENT}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={fieldValueTestId("answer-char-count")}>
                    {(editingData.answer || "").length}/{CHARACTER_LENGTH.CONTENT}
                  </p>
                </div>
              </div>
            )}
            <div className={`space-y-0`} data-testid="faq-detail-metadata-section">
              <div className={`grid grid-cols-5 gap-8 pt-4`} data-testid="faq-detail-metadata-grid">
                <div className="min-w-0" data-testid="faq-detail-creator-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("common.creator")}
                  </span>
                  {isEditMode ? (
                    <CustomInput
                      data-testid="faq-detail-creator-input"
                      type="text"
                      disabled={true}
                      value={formatUserNameWithId(faqData?.createrName, faqData?.createrId)}
                    />
                  ) : (
                    <UserNameWithId
                      name={faqData?.createrName}
                      id={faqData?.createrId}
                      layout="inline"
                      data-testid="faq-detail-creator-value"
                    />
                  )}
                </div>

                <div data-testid="faq-detail-creation-date-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("bulletin.creation-date")}
                  </span>
                  {isEditMode ? (
                    <CustomInput
                      data-testid="faq-detail-creation-date-input"
                      type="text"
                      disabled={true}
                      value={formatDateTime(faqData?.createdAt) || ""}
                    />
                  ) : (
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid="faq-detail-creation-date-value"
                    >
                      {formatDateTime(faqData?.createdAt) || "---"}
                    </span>
                  )}
                </div>

                <div className="min-w-0" data-testid="faq-detail-last-editor-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("common.last-modified-by")}
                  </span>
                  {isEditMode ? (
                    <CustomInput
                      data-testid="faq-detail-last-editor-input"
                      type="text"
                      disabled={true}
                      value={formatUserNameWithId(faqData?.updaterName, faqData?.updaterId)}
                    />
                  ) : (
                    <UserNameWithId
                      name={faqData?.updaterName}
                      id={faqData?.updaterId}
                      layout="inline"
                      data-testid="faq-detail-last-editor-value"
                    />
                  )}
                </div>

                <div data-testid="faq-detail-last-modified-field">
                  <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                    {t("common.last-modified-date")}
                  </span>
                  {isEditMode ? (
                    <CustomInput
                      data-testid="faq-detail-last-modified-input"
                      type="text"
                      disabled={true}
                      value={formatDateTime(faqData?.updatedAt) || ""}
                    />
                  ) : (
                    <span
                      className={`text-sm font-medium ${colors.textMessage}`}
                      data-testid="faq-detail-last-modified-value"
                    >
                      {formatDateTime(faqData?.updatedAt) || "---"}
                    </span>
                  )}
                </div>

                <div data-testid="faq-detail-view-count-field">
                  {!isEditMode && (
                    <>
                      <span className={`text-[0.8125rem] font-normal ${colors.textSubTitle} block mb-1`}>
                        {t("bulletin.view-count")}
                      </span>
                      <span
                        className={`text-sm font-medium ${colors.textMessage}`}
                        data-testid="faq-detail-view-count-value"
                      >
                        {faqData?.viewCnt || "0"}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* File Information Section */}
      <div
        className={`rounded-3xl border ${colors.borderPrimary} ${colors.bgPrimary} px-6 pt-6 pb-2 mt-6`}
        data-testid="faq-detail-attachments-section"
      >
        <div className="flex items-center justify-between mb-2" data-testid="faq-detail-attachments-toolbar">
          <h2
            className={`font-medium ${colors.textPrimary}`}
            data-testid="faq-detail-attachments-title"
          >
            {uppercase(t("bulletin.attachment"))} ({documentTotalData || 0})
          </h2>
          <OutlineButton data-testid="faq-detail-upload-file-button" onClick={handleUploadFile}>
            {t("project.document.upload-file")}
          </OutlineButton>
        </div>

        {fileInfoModified && fileInfoModified.length > 0 ? (
          <div data-testid="faq-detail-documents-table">
          <DocumentTable
            data={fileInfoModified}
            onDelete={handleDeleteDocument}
            currentPage={documentCurrentPage}
            totalPages={Math.ceil((documentTotalData || 0) / documentItemsPerPage)}
            onPageChange={setDocumentCurrentPage}
            totalData={documentTotalData}
          />
          </div>
        ) : (
          <div
            className={`py-8 text-center ${colors.textSecondary}`}
            data-testid="faq-detail-no-documents"
          >
            <p>{t("project.no-documents-found")}</p>
          </div>
        )}
      </div>

      {/* Confirm Delete Document Modal */}
      <ConfirmDeleteModal
        data-testid="faq-detail-delete-document-modal"
        isOpen={showDeleteDocumentModal}
        onClose={() => setShowDeleteDocumentModal(false)}
        onConfirm={handleConfirmDeleteDocument}
        title={t("bulletin.delete-document")}
        description={t("bulletin.delete-document-description")}
        loading={deleteDocumentLoading}
      />
      <FullScreenDotLoader
        loading={uploadFilesAndGetOnSuccess.isPending}
        text={
          uploadFilesAndGetOnSuccess.isPending
            ? t("bulletin.uploading-faq") || "Uploading FAQ file..."
            : t("bulletin.upload-faq-file") || "Uploading FAQ file..."
        }
        size={80}
        speedMultiplier={1.2}
      />
    </div>
  );
}
